import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { ProtectedRoute, RoleHome } from "@/components/ProtectedRoute";

import Login from "@/pages/Login";
import AdminLayout from "@/components/AdminLayout";
import DriverLayout from "@/components/DriverLayout";

import Dashboard from "@/pages/admin/Dashboard";
import Drivers from "@/pages/admin/Drivers";
import DriverDetail from "@/pages/admin/DriverDetail";
import Vehicles from "@/pages/admin/Vehicles";
import DpdImport from "@/pages/admin/DpdImport";
import PayPeriods from "@/pages/admin/PayPeriods";
import PayPeriodDetail from "@/pages/admin/PayPeriodDetail";
import Deductions from "@/pages/admin/Deductions";
import Invoices from "@/pages/admin/Invoices";
import Documents from "@/pages/admin/Documents";
import Holidays from "@/pages/admin/Holidays";
import CompanySettings from "@/pages/admin/CompanySettings";
import DpdAccounts from "@/pages/admin/DpdAccounts";
import DpdManualLogin from "@/pages/admin/DpdManualLogin";
import DpdSelectors from "@/pages/admin/DpdSelectors";
import Users from "@/pages/admin/Users";
import Companies from "@/pages/admin/Companies";
import Profile from "@/pages/admin/Profile";

import DriverHome from "@/pages/driver/Home";
import DriverWork from "@/pages/driver/Work";
import DriverPay from "@/pages/driver/Pay";
import DriverInvoices from "@/pages/driver/Invoices";
import DriverHolidays from "@/pages/driver/Holidays";
import DriverMore from "@/pages/driver/More";
import DriverAddressFinder from "@/pages/driver/AddressFinder";
import DriverAddressFinderAdd from "@/pages/driver/AddressFinderAdd";
import DriverAddressFinderDetail from "@/pages/driver/AddressFinderDetail";
import DriverAddressFinderQuickFind from "@/pages/driver/AddressFinderQuickFind";
import AdminContracts from "@/pages/admin/Contracts";
import AdminBusinessProtection from "@/pages/admin/BusinessProtection";
import AdminPenalties from "@/pages/admin/Penalties";
import AdminWorkPlanner from "@/pages/admin/WorkPlanner";
import AdminOnboarding from "@/pages/Onboarding";
import DriverOnboarding from "@/pages/driver/OnboardingPage";
import DriverContracts from "@/pages/driver/Contracts";
import DriverPenalties from "@/pages/driver/Penalties";
import PublicSign from "@/pages/PublicSign";
import AuthCallback from "@/pages/AuthCallback";
import OfflineStatus from "@/components/OfflineStatus";
import FuelAdmin from "@/pages/admin/Fuel";
import DriverFuelReceipts from "@/pages/driver/FuelReceipts";
import UploadIssue from "@/pages/driver/UploadIssue";
import Notifications from "@/pages/Notifications";
import UnifiedCapture from "@/pages/driver/UnifiedCapture";
import SharedUpload from "@/pages/SharedUpload";
import { useLocation } from "react-router-dom";

import "@/App.css";

// Synchronous hash guard — if the Emergent Google OAuth redirect landed us
// with a `#session_id=` fragment, render the AuthCallback exchanger BEFORE
// any route (including ProtectedRoute) runs. useEffect is too late: it runs
// after first render, by which time ProtectedRoute already redirected.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH
const HashOAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
        <BrowserRouter>
          <HashOAuthGate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sign/:token" element={<PublicSign />} />
            <Route path="/shared-upload" element={<ProtectedRoute roles={["platform_owner", "company_admin", "depot_manager", "driver"]}><SharedUpload /></ProtectedRoute>} />

            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["platform_owner", "company_admin", "depot_manager"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="drivers/:id" element={<DriverDetail />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="dpd-import" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><DpdImport /></ProtectedRoute>} />
              <Route path="pay-periods" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><PayPeriods /></ProtectedRoute>} />
              <Route path="pay-periods/:id" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><PayPeriodDetail /></ProtectedRoute>} />
              <Route path="deductions" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><Deductions /></ProtectedRoute>} />
              <Route path="invoices" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><Invoices /></ProtectedRoute>} />
              <Route path="documents" element={<Documents />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="users" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><Users /></ProtectedRoute>} />
              <Route path="companies" element={<ProtectedRoute roles={["platform_owner"]}><Companies /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><CompanySettings /></ProtectedRoute>} />
              <Route path="contracts" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><AdminContracts /></ProtectedRoute>} />
              <Route path="business-protection" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><AdminBusinessProtection /></ProtectedRoute>} />
              <Route path="penalties" element={<ProtectedRoute roles={["platform_owner", "company_admin", "depot_manager"]}><AdminPenalties /></ProtectedRoute>} />
              <Route path="fuel" element={<FuelAdmin />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="work-planner" element={<ProtectedRoute roles={["platform_owner", "company_admin", "depot_manager"]}><AdminWorkPlanner /></ProtectedRoute>} />
              <Route path="onboarding/:id" element={<AdminOnboarding />} />
              <Route path="integrations/dpd" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><DpdAccounts /></ProtectedRoute>} />
              <Route path="integrations/dpd/:accountId/manual-login" element={<ProtectedRoute roles={["platform_owner", "company_admin"]}><DpdManualLogin /></ProtectedRoute>} />
              <Route path="integrations/dpd/selectors" element={<ProtectedRoute roles={["platform_owner"]}><DpdSelectors /></ProtectedRoute>} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route
              path="/driver"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <DriverLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DriverHome />} />
              <Route path="work" element={<DriverWork />} />
              <Route path="pay" element={<DriverPay />} />
              <Route path="invoices" element={<DriverInvoices />} />
              <Route path="holidays" element={<DriverHolidays />} />
              <Route path="more" element={<DriverMore />} />
              <Route path="address-finder" element={<DriverAddressFinder />} />
              <Route path="address-finder/quick-find" element={<DriverAddressFinderQuickFind />} />
              <Route path="address-finder/add" element={<DriverAddressFinderAdd />} />
              <Route path="address-finder/:id" element={<DriverAddressFinderDetail />} />
              <Route path="contracts" element={<DriverContracts />} />
              <Route path="penalties" element={<DriverPenalties />} />
              <Route path="fuel" element={<DriverFuelReceipts />} />
              <Route path="upload-issue" element={<UploadIssue />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="camera" element={<UnifiedCapture />} />
              <Route path="onboarding" element={<DriverOnboarding />} />
            </Route>

            <Route path="/" element={<RoleHome />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </HashOAuthGate>
          <Toaster position="top-right" richColors closeButton />
          <OfflineStatus />
        </BrowserRouter>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
